// Wave 20.47b — mint a replay drop link.
//
// Owner picks expiration window + comment permission, hits Drop,
// gets a URL that anyone (logged in or not) can open at
// /replay/:token. Language matches the rest of the app: everything
// is a "drop", never a "share".

import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import { colors } from '../theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

const SHARE_WINDOWS: Array<{ value: string; label: string }> = [
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: 'forever', label: 'Never expires' },
];

function auth(): Record<string, string> {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

interface Props {
  open: boolean;
  onClose: () => void;
  sessionId: string;
}

export default function ShareReplayDialog({ open, onClose, sessionId }: Props) {
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [windowChoice, setWindowChoice] = useState('forever');
  const [minting, setMinting] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mint = async () => {
    setMinting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/replay-shares`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, commentsEnabled, window: windowChoice }),
      });
      if (!res.ok) throw new Error(`Drop failed (${res.status})`);
      const data = await res.json();
      setUrl(data.url);
      setExpiresAt(data.expiresAt ?? null);
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Try again in a moment.');
    } finally {
      setMinting(false);
    }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* clipboard denied */
    }
  };

  const close = () => {
    setUrl(null);
    setExpiresAt(null);
    setError(null);
    setCommentsEnabled(true);
    setWindowChoice('forever');
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Drop replay</DialogTitle>
        <DialogContent>
          {!url ? (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography sx={{ color: colors.textMuted }}>
                Anyone with the link can watch the full replay — no login needed. Viewers can send cheers; comments (optional) let them leave notes at specific points along the ride.
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ color: colors.textMuted, minWidth: 90, fontSize: '0.85rem' }}>Expires in</Typography>
                <Select
                  value={windowChoice}
                  onChange={(e) => setWindowChoice(e.target.value)}
                  size="small"
                  fullWidth
                >
                  {SHARE_WINDOWS.map((w) => (
                    <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={commentsEnabled}
                    onChange={(e) => setCommentsEnabled(e.target.checked)}
                    sx={{ '& .MuiSwitch-thumb': { color: colors.brand } }}
                  />
                }
                label="Allow comments on the replay"
              />
              {error && <Typography color="error">{error}</Typography>}
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography sx={{ color: colors.textMuted }}>
                Your drop is ready.{expiresAt ? ` It expires ${new Date(expiresAt).toLocaleString()}.` : ' It never expires.'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField value={url} fullWidth size="small" InputProps={{ readOnly: true }} />
                <Button
                  variant="contained"
                  onClick={copy}
                  startIcon={<ContentCopyIcon />}
                  sx={{ backgroundColor: colors.brand, color: '#0a100c' }}
                >
                  Copy
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{url ? 'Done' : 'Cancel'}</Button>
          {!url && (
            <Button onClick={mint} disabled={minting} variant="contained" sx={{ backgroundColor: colors.brand, color: '#0a100c' }}>
              {minting ? 'Dropping…' : 'Drop it'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar open={copied} autoHideDuration={2200} onClose={() => setCopied(false)} message="Copied to clipboard" />
    </>
  );
}
